import { Component, OnInit, inject } from "@angular/core";
import { BucketItemService } from "../service/bucket-item.service";
import { catchError, finalize, map, throwError } from "rxjs";
import { IBucketItem } from "../models/bucket-item.model";
import {
  IFileVersionHistoryRequest,
  ITagVersion,
  IVersion,
} from "../models/file-version-history.model";
import { RowDataService } from "../service/row-data.service";
import { BreadcrumbDataService } from "../../../main/layout/tool-bar/breadcrumb-service/breadcrumb-data.service";
import { IBreadcrumbItem } from "../../../main/layout/tool-bar/model/breadcrumb.model";
import { Clipboard } from "@angular/cdk/clipboard";
import { MessageService } from "../../../main/services/message.service";
import {
  CommonConsants,
  ErrorConstants,
} from "../constants/component-constants";
import { AppSettingsService } from "../../../core/config/app-settings.service";
import { OverlayPopupService } from "../../../core/overlay-popup/overlay-popup.service";
import { ListOverlayComponent } from "../list-overlay/list-overlay.component";
import { HttpErrorResponse } from "@angular/common/http";
import { SpinnerService } from "../../../core";
import { IFileVersionChecksum } from "../models/file-version-checksum.model";
import {
  IBucketItemVersion,
  IDownloadFileversion,
} from "../models/download-file-version.model";
import { JustificationDialogComponent } from "../../../shared/justification-dialog/justification-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { FileVersionInformationComponent } from "../file-version-information/file-version-information.component";
import { GenericExtensionsService } from "../../../extensions/generic-extensions-service";
import { ILargeFileDownloadExpandedRequest, ILargeFileDownloadRequest, ILargeFileDownloadResponse } from "../models/download-item.model";
import { MatDivider } from "@angular/material/divider";
import { NoDocumentFoundComponent } from "../../../shared/no-document-found/no-document-found.component";
import { TooltipDisableDirective } from "../../../shared/custom-directives/tooltip.directive";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIconButton } from "@angular/material/button";
import { MatChipListbox, MatChipOption } from "@angular/material/chips";
import { MatRippleDirective } from "../../../shared/custom-directives/matRipple.directive";
import { DatePipe } from "@angular/common";

@Component({
    selector: "app-file-version-history",
    templateUrl: "./file-version-history.component.html",
    styleUrls: ["./file-version-history.component.scss"],
    imports: [MatDivider, NoDocumentFoundComponent, TooltipDisableDirective, MatTooltip, MatIconButton, MatChipListbox, MatChipOption, MatRippleDirective, DatePipe]
})
export class FileVersionHistoryComponent implements OnInit {
  private bucketItemService = inject(BucketItemService);
  private dataService = inject(RowDataService);
  private breadcrumbDataService = inject(BreadcrumbDataService);
  private clipboard = inject(Clipboard);
  private messageService = inject(MessageService);
  private appSettingService = inject(AppSettingsService);
  private readonly overlayPopupService = inject(OverlayPopupService);
  private spinnerService = inject(SpinnerService);
  private dialog = inject(MatDialog);
  private genericExtensionService = inject(GenericExtensionsService);

  private readonly featureToken = "bucket-items";
  private readonly surfaceToken = "file-version-history";

  requestData: Partial<IFileVersionHistoryRequest> = {};
  versions: Partial<IVersion>[] = [];
  isChecksumMismatchForFileVersion: boolean = false;
  bucketId: string = "";
  downloadFileVersionInput: Partial<IDownloadFileversion> = {};

  ngOnInit(): void {
    this.getSelectedRowDataFromService();
    this.getSelectedRowDataFromBreadCrumbData();
    this.getFileVersionData();
  }

  getSelectedRowDataFromService(): void {
    this.dataService
      .getClickedRowData()
      .pipe(
        map((bucketItem: Partial<IBucketItem>) => {
          this.requestData.BucketItemId = bucketItem.Id;
          this.requestData.PackageId = bucketItem.PackageId;
        })
      )
      .subscribe();
  }

  getSelectedRowDataFromBreadCrumbData(): void {
    this.breadcrumbDataService
      .getCurrentBreadCrumbItem()
      .pipe(
        map((breadCrumbData: Partial<IBreadcrumbItem>) => {
          this.requestData.BucketType = breadCrumbData.BucketType;
          this.requestData.BUCode = breadCrumbData.BUCode;
          this.bucketId = breadCrumbData.BucketId;
        })
      )
      .subscribe();
  }

  getFileVersionData(): void {
    this.spinnerService.show(true);
    if (localStorage.getItem("requesterPackageFolderComponent")==='true'){
      this.requestData.IsFromRequesterPage = true;
    }
    this.bucketItemService
      .GetFileVersionHistory(this.requestData)
      .pipe(
        map((versions: Partial<IVersion>[]) => {
          this.versions = [...versions];
          this.versions.forEach((x) => {
            x.FileName = x.PhysicalFileName.substring(37);
            x.Size = x.Size;
            x.FileLastModifiedOn = x.FileLastModifiedOn;
            x.UIVersion = x.UIVersion.toUpperCase();
            x.TagVersions = x.TagVersions.filter((x) => x.TagValue !== "");
            x.TagsCount = x.TagVersions.length;
            x.TagsToBeDisplayed =
              x.TagsCount > 2
                ? [...x.TagVersions.slice(0, 2)]
                : [...x.TagVersions];
          });
          this.spinnerService.show(false);
        }),
        catchError(async (error: HttpErrorResponse) => {
          this.spinnerService.show(false);
          if (error.error.detail) {
            this.messageService.alert(
              error.error.detail,
              this.appSettingService.toastrDisplayTime
            );
          } else {
            this.messageService.alert(
              error.error.message,
              this.appSettingService.toastrDisplayTime
            );
          }
          return throwError(() => new Error(error.message));
        })
      )
      .subscribe();
  }

  copyToClipBoard(streamhash: string) {
    this.clipboard.copy(streamhash);
    this.messageService.success(
      CommonConsants.COPY_CLIPBOARD_SUCCESS,
      this.appSettingService.toastrDisplayTime
    );
  }

  regenerateChecksum(versionId: string): void {
    this.spinnerService.show(true);
    let id: string = this.versions.find((x) => x.Id === versionId).Id;
    let requestData: IFileVersionChecksum = {
      VersionId: id,
      PackageId: this.requestData.PackageId,
      BucketItemId: this.requestData.BucketItemId,
    };
    this.bucketItemService
      .RegenerateChecksumForFieVersion(requestData)
      .pipe(
        map((response: boolean) => {
          this.versions.find((x) => x.Id === versionId).ChecksumStatus =
            response ? "VerifiedAndCorrupted" : "VerifiedAndMatching";
          this.spinnerService.show(false);
          if (
            this.versions.find((x) => x.Id === versionId).ChecksumStatus === "VerifiedAndCorrupted"
          ) {
            this.messageService.alert(
              CommonConsants.CHECKSUM_MISMATCH,
              this.appSettingService.toastrDisplayTime
            );
          } else {
            this.messageService.success(
              CommonConsants.CHECKSUM_MATCH,
              this.appSettingService.toastrDisplayTime
            );
          }
        }),
        catchError(async (error: HttpErrorResponse) => {
          this.spinnerService.show(false);
          this.versions.find((x) => x.Id === versionId).ChecksumStatus =
            "VerifiedAndCorrupted";
          this.messageService.alert(
            CommonConsants.CHECKSUM_MISMATCH,
            this.appSettingService.toastrDisplayTime
          );
          return throwError(() => new Error(error.message));
        })
      )
      .subscribe();
  }

  overlaydisplay(tagVersions: Partial<ITagVersion>[], e: any) {
    var tagValues = tagVersions.reduce((val, tag) => {
      return [...val, tag.TagValue];
    }, []);
    this.overlayPopupService.open(ListOverlayComponent, {
      data: tagValues,
      element: e,
      offsetStartY: 10,
    });
  }

  downloadFileVersion(uiVersion: string): void {
    this.spinnerService.show(true);
    const selectedVersion = this.versions.find(v => v.UIVersion === uiVersion);
    if (!selectedVersion) {
      this.messageService.alert("Selected version not found.", this.appSettingService.toastrDisplayTime);
      this.spinnerService.show(false);
      return;
    }

    const totalSize = selectedVersion.Size || 0; // Use version size
    const ONE_GIGABYTE = 1 * 1024 * 1024 * 1024; // 1 GB in bytes

    if (totalSize > ONE_GIGABYTE) {
      // Large file download logic
      const largeFilePayload: ILargeFileDownloadRequest[] = [{
        BucketItemId: this.requestData.BucketItemId,
        Version: uiVersion
      }];

      const largeFilePayloadExpanded: ILargeFileDownloadExpandedRequest = {
        FileDownloadRequests: largeFilePayload,
        BucketType: this.requestData.BucketType,
        BUCode: this.requestData.BUCode
      };

      if (localStorage.getItem("requesterPackageFolderComponent")==='true'){
        largeFilePayloadExpanded.IsFromRequesterPage = true;
      }

      this.bucketItemService.registerLargeFileDownload(largeFilePayloadExpanded)
        .pipe(
          finalize(() => this.spinnerService.show(false))
        )
        .subscribe({
          next: (response: ILargeFileDownloadResponse) => {
            if (response.isRequestSuccessful) {
              this.messageService.success(
                response.statusMessage,
                this.appSettingService.toastrDisplayTime
              );
            } else {
              this.messageService.alert(
                response.statusMessage || ErrorConstants.DOWNLOAD_FAILED,
                this.appSettingService.toastrDisplayTime
              );
            }
          },
          error: (error: HttpErrorResponse) => {
            let displayMessage = ErrorConstants.DOWNLOAD_FAILED;
            try {
              const errorBody = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;
              displayMessage = errorBody?.detail ?? error.message ?? displayMessage;
            } catch (parseError) {
              if (error.message) {
                displayMessage = error.message;
              }
            }
            this.messageService.alert(
              displayMessage,
              this.appSettingService.toastrDisplayTime
            );
          }
        });
    } else {
      // Existing download logic for files <= 1 GB
      let bucketItemVersion: IBucketItemVersion[] = [
        { BucketItemId: this.requestData.BucketItemId, Version: uiVersion },
      ];
      this.downloadFileVersionInput.BucketId = this.bucketId;
      this.downloadFileVersionInput.BucketType = this.requestData.BucketType;
      this.downloadFileVersionInput.BuCode = this.requestData.BUCode;
      this.downloadFileVersionInput.PackageId = this.requestData.PackageId;
      this.downloadFileVersionInput.BucketItemsVersion = [...bucketItemVersion];

      if (localStorage.getItem("requesterPackageFolderComponent")==='true'){
        this.downloadFileVersionInput.IsFromRequesterPage = true;
      }
      this.bucketItemService
        .DownloadFileVersion(this.downloadFileVersionInput)
        .pipe(
          map((data: any) => {
            if (data) {
              if (data.body.type === "application/json") {
                this.spinnerService.show(false); // Hide spinner before dialog
                this.dialog.open(JustificationDialogComponent, {
                  width: "450px",
                  disableClose: true
                }).afterClosed().subscribe(
                  (response: { comment: string }) => {
                    if (response && response.comment) {
                      this.downloadFileVersionInput.Comment = response.comment;
                      //It will make the process continue after giving the justification
                      this.downloadFileVersion(uiVersion); // Re-trigger download with comment
                    } else {
                      this.spinnerService.show(false); // Hide spinner if justification is cancelled (redundant as it's hidden before dialog, but safe)
                    }
                  }
                );
              } else {
                this.spinnerService.show(false); // Hide spinner after processing
                this.downloadFileVersionInput.Comment = null;
                const contentDisposition = data.headers.get("Content-Disposition");
                let filename =
                  this.getFilenameFromContentDisposition(contentDisposition);
                filename = this.genericExtensionService.extractBase64AndDecode(filename);
                const blob = new Blob([data.body], {
                  type: "application/zip",
                });
                const a: any = document.createElement("a");
                const url = window.URL.createObjectURL(blob);
                a.href = url;
                a.download = filename;
                a.click();
                window.URL.revokeObjectURL(url);
              }
            } else {
              this.spinnerService.show(false); // Hide spinner if no data
            }
          }),
          catchError(async (error: HttpErrorResponse) => {
            this.spinnerService.show(false); // Ensure spinner hides on error
            this.downloadFileVersionInput.Comment = null; // Reset comment on error
            var errorMessage = JSON.parse(await error?.error?.text());
            if (errorMessage.detail) {
              if (
                errorMessage.detail.toString().startsWith(
                  ErrorConstants.DOWNLOAD_SIZE_REACHED
                )
              ) {
                this.messageService.confirm(
                  this.formatMessage(errorMessage.detail),
                  ErrorConstants.MAX_DOWNLOAD_LIMIT,
                  "",
                  "OK",
                  false
                );
              } else {
                this.messageService.alert(
                  errorMessage.detail,
                  this.appSettingService.toastrDisplayTime
                );
              }
            } else {
              this.messageService.alert(
                ErrorConstants.DOWNLOAD_FAILED,
                this.appSettingService.toastrDisplayTime
              );
            }
            return throwError(() => new Error(error.message || 'Download failed'));
          }),
          finalize(() => {
          })
        )
        .subscribe();
    }
  }

  private getFilenameFromContentDisposition(
    contentDisposition: string
  ): string {
    const regex = /filename=(?<filename>[^,;]+);/g;
    const match = regex.exec(contentDisposition);
    const filename = match.groups.filename;
    return filename.replace(/"/g, "");
  }

  public formatMessage(message: string): string {
    let formattedTxtArr = message.split('"');
    formattedTxtArr[1] = `<span class="blue-text">${formattedTxtArr[1]}</span>`;
    var parts = formattedTxtArr[3].split(" ");
    var numberPart = parts[0];
    var ellipsisNumber = numberPart.slice(0, 4) + "...";
    var formattedText = ellipsisNumber + " " + parts.slice(1).join(" ");
    formattedTxtArr[3] = `<span class="red-text tooltip-confirm-popup ">${formattedText}
                          <span class="tooltiptext-confirm-popup font-s radius-4">${formattedTxtArr[3]}</span>
                          </span>`
    return formattedTxtArr.join('"');
  }

  onInfoClick(
    e: any,
    fileType: string,
    fileLastModifiedOn: Date,
    fileSize: number
  ): void {
    e.preventDefault();

    let formattedFileSize = this.genericExtensionService.formatBytes(fileSize);
    let sizeInBytes = fileSize.toString() + ' Bytes';
    const itemList: [string, string, string, Date] = [
      fileType,
      formattedFileSize,
      sizeInBytes,
      fileLastModifiedOn
    ];

    this.overlayPopupService.open(FileVersionInformationComponent, {
      element: e,
      displayPointer: false,
      data: itemList,
      offsetStartX: 84,
      offsetStartY: 15,
    });
  }

  public buildControlId(element: string, context?: string | number): string {
    const tokens = [
      this.featureToken,
      this.surfaceToken,
      this.slugify(element),
    ];
    if (context !== undefined && context !== null) {
      tokens.push(this.formatContextToken(context));
    }
    return tokens.join('-');
  }

  private formatContextToken(value: string | number): string {
    if (typeof value === 'number') {
      return value.toString().padStart(2, '0');
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      return this.slugify(value);
    }
    return '00';
  }

  private slugify(value: string): string {
    return (value || 'na')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');
  }
}
